<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import DetailedJobBlockWrapper from '@/components/wrappers/DetailedJobBlockWrapper.vue';
import * as commonConstants from '@/constants/common';
import * as customerConstants from '@/constants/customer';

export interface Props {
  job?: ReadonlyDeep<
    RequiredKeys<
      Partial<Pick<openapi.JobDespatch, 'rockhopper_date' | 'status'>>
    >
  >;
  detailedCustomer?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<openapi.JobDespatch['customer'], 'name' | 'code' | 'highlight'>
      >
    >
  >;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  detailedCustomer: undefined,
  isStaff: false,
});

const job = computed<Partial<NonNullable<Props['job']>>>(() => props.job || {});
const detailedCustomer = computed<
  Partial<NonNullable<Props['detailedCustomer']>>
>(() => props.detailedCustomer || {});
</script>

<!-- TODO similar to @/components/descLists/DetailedJobBaseInfoDescList.vue -->
<template>
  <dl
    :class="$style.dispatchJobBaseInfoDescList"
    data-testid="dispatchJobBaseInfoDescList"
  >
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-account-outline"></v-icon>
        </div>
        Customer
      </dt>
      <dd :class="$style.dd">
        <template v-if="detailedCustomer.code">
          <span>
            <KParentTooltip location="top">
              {{ detailedCustomer.name }}
            </KParentTooltip>
            {{ detailedCustomer.code }}
          </span>
          <sup
            v-if="props.isStaff && detailedCustomer.highlight"
            :class="$style.sup"
          >
            {{ customerConstants.highlightIcons[detailedCustomer.highlight] }}
            <KParentTooltip location="right">
              {{
                customerConstants.highlightTooltips[detailedCustomer.highlight]
              }}
            </KParentTooltip>
          </sup>
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-calendar-range"></v-icon>
        </div>
        Job date
      </dt>
      <dd :class="$style.dd">
        <template v-if="job.rockhopper_date">
          {{ job.rockhopper_date }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
    <DetailedJobBlockWrapper :class="$style.item">
      <dt :class="$style.dt">
        <div :class="$style.iconWrapper">
          <v-icon :class="$style.icon" icon="mdi-map-marker-outline"></v-icon>
        </div>
        Status
      </dt>
      <dd :class="$style.dd">
        <template v-if="job.status">
          {{ job.status }}
        </template>
        <template v-else>{{ commonConstants.EM_DASH }}</template>
      </dd>
    </DetailedJobBlockWrapper>
  </dl>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchJobBaseInfoDescList {
  margin: 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;
}
    .iconWrapper {
      background-color: colors.$color-gray-100;
      color: colors.$color-text-primary;
      font-size: 11px;
      line-height: 1;
      padding: 2px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .item {
      padding: 8px 12px;
    }
      .dt {
        font-size: 16px;
        font-weight: 500;
        color: colors.$color-text-tertiary;
        display: flex;
        gap: 8px;
      }
      .dd {
        margin: 2px 0 0 0;
        font-size: 16px;
        font-weight: 500;
        color: colors.$color-text-primary;
      }
        .sup {
          font-size: 12px;
          position: relative;
          top: -6px;
        }
</style>
